import React from 'react';
import SectionTag from '../components/SectionTag';
import ScrollReveal from '../components/ScrollReveal';
import PhoneFrame from '../components/PhoneFrame';

const FEATURES = [
  {
    tag: 'ONBOARDING', number: '01',
    heading: 'Sign Up in Minutes',
    desc: 'Getting started is effortless. Create your free account with just the basics and instantly unlock access to local resources and community events near you.',
    bullets: ['Simple and fast sign up', 'No unnecessary information required', 'Instant access after registration'],
    image: 'create-account.png',
    badge: 'Free to Join',
    stats: { label: 'Service Categories', value: '8', sub: 'Find what you need' },
  },
  {
    tag: 'DISCOVERY', number: '02',
    heading: 'Find What You Need, Fast',
    desc: 'A clean, organized feed of resources and events posted by verified non-profit organizations. Filter by category and find exactly the support you\'re looking for.',
    bullets: ['Browse verified non-profit listings', 'Organized by category and type', 'Always up to date'],
    image: 'browse.png',
    badge: 'Browse Resources',
    stats: { label: 'Available Categories', value: '9', sub: 'Near your location' },
  },
  {
    tag: 'INFORMATION', number: '03',
    heading: 'Know Before You Go',
    desc: 'Every listing comes with complete information — the event name, a rich description, a banner image, and the exact date and time — so you always feel prepared and informed before attending.',
    bullets: ['Full event description and banner', 'Clear date and time display', 'Non-profit contact information'],
    image: 'event-detail.png',
    badge: 'Food Drive',
    stats: { label: 'Distance', value: '2.6mi', sub: 'Open · May 20, 2026' },
  },
  {
    tag: 'CONNECTION', number: '04',
    heading: 'Ask. Connect. Get Help.',
    desc: 'Have a question before attending? Send a direct inquiry to the non-profit that posted the event or resource. Get a real answer from real people who are ready to help you.',
    bullets: ['Direct message to the non-profit', 'Simple inquiry form', 'Get responses quickly'],
    image: 'send-inquiry.png',
    badge: 'Inquiry Form',
    stats: { label: 'Response Time', value: 'Fast', sub: 'Real people, real help' },
  },
];

function AppFeatureRow({ feature, index }) {
  const isOdd = index % 2 === 0;

  const textCol = (
    <ScrollReveal direction={isOdd ? 'left' : 'right'} delay={0}>
      <div style={{ maxWidth: '480px' }}>
        <div
          className="inline-block mb-3"
          style={{
            background: 'rgba(254,92,115,0.1)', color: '#FE5C73',
            fontFamily: 'popinsMedium', fontSize: '12px',
            padding: '4px 12px', borderRadius: '6px', letterSpacing: '0.06em',
          }}
        >
          {feature.tag}
        </div>
        <div className="relative mb-3">
          <span
            className="absolute select-none pointer-events-none"
            style={{ fontFamily: 'popinsBold', fontSize: '48px', color: 'rgba(254,92,115,0.12)', top: '-20px', left: '-8px', lineHeight: 1 }}
          >
            {feature.number}
          </span>
          <h3 style={{ fontFamily: 'popinsBold', fontSize: 'clamp(20px, 2.5vw, 28px)', color: '#1B1A28', lineHeight: 1.2, position: 'relative', zIndex: 1 }}>
            {feature.heading}
          </h3>
        </div>
        <p style={{ fontFamily: 'popinsRegular', fontSize: '16px', color: '#6B7280', lineHeight: '1.8', marginBottom: '20px' }}>
          {feature.desc}
        </p>
        <ul className="flex flex-col gap-2">
          {feature.bullets.map((b) => (
            <li key={b} className="flex items-center gap-2">
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#FE5C73', flexShrink: 0, display: 'inline-block' }} />
              <span style={{ fontFamily: 'popinsRegular', fontSize: '14px', color: '#6B7280' }}>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </ScrollReveal>
  );

  const phoneCol = (
    <ScrollReveal direction={isOdd ? 'right' : 'left'} delay={200}>
      <PhoneFrame
        src={`/app/${feature.image}`}
        alt={feature.heading}
        badge={feature.badge}
        stats={feature.stats}
      />
    </ScrollReveal>
  );

  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center feature-row" style={{ padding: '80px 0' }}>
        {isOdd
          ? <><div className="flex justify-start">{textCol}</div><div className="flex justify-center">{phoneCol}</div></>
          : <><div className="flex justify-center order-last lg:order-first">{phoneCol}</div><div className="flex justify-end order-first lg:order-last">{textCol}</div></>
        }
      </div>
      <div style={{ height: '1px', background: '#E5E5E5' }} />
    </div>
  );
}

export default function PeopleInNeed() {
  return (
    <section id="people-in-need" style={{ background: '#F8FAFC', padding: '120px 5% 80px' }}>
      <div className="max-w-screen-xl mx-auto">
        <div className="flex flex-col items-center text-center mb-4">
          <SectionTag label="PEOPLE IN NEED APP" />
          <h2 style={{ fontFamily: 'popinsBold', fontSize: 'clamp(26px, 4vw, 44px)', color: '#1B1A28', marginBottom: '12px' }}>
            Help Is Always In Your Pocket
          </h2>
          <p style={{ fontFamily: 'popinsRegular', fontSize: '16px', color: '#6B7280', maxWidth: '600px', textAlign: 'center' }}>
            The TCCplus People In Need app makes it simple to discover local resources, explore upcoming events, and connect with non-profits — anytime, anywhere.
          </p>
        </div>

        {FEATURES.map((feature, i) => (
          <AppFeatureRow key={feature.number} feature={feature} index={i} />
        ))}

        {/* Download CTA */}
        <ScrollReveal direction="up">
          <div className="flex flex-col items-center text-center mt-16 gap-4">
            <h3 style={{ fontFamily: 'popinsSemiBold', fontSize: 'clamp(20px, 3vw, 28px)', color: '#1B1A28' }}>
              Available on iOS & Android
            </h3>
            <p style={{ fontFamily: 'popinsRegular', fontSize: '15px', color: '#6B7280' }}>
              Download the TCCplus People In Need app and start finding support near you.
            </p>
            <div className="flex items-center gap-4 flex-wrap justify-center mt-2">
              {/* App Store */}
              <a
                href="https://apps.apple.com/us/app/tccplus/id6768018763"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-all duration-300"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '12px',
                  background: '#1B1A28', border: '1px solid #E5E5E5',
                  borderRadius: '14px', padding: '14px 28px', textDecoration: 'none',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#3FC4E3'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(63,196,227,0.2)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E5E5'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; }}
              >
                <svg width="22" height="26" viewBox="0 0 22 26" fill="none" aria-hidden="true">
                  <path d="M18.07 13.77C18.05 11.05 19.69 9.36 21 8.34C20.05 6.96 18.61 6.17 17.22 6.09C15.74 5.98 14.3 6.96 13.55 6.96C12.79 6.96 11.57 6.11 10.33 6.13C8.74 6.16 7.25 7.08 6.44 8.52C4.77 11.44 6.05 15.75 7.66 18.11C8.46 19.27 9.4 20.57 10.65 20.52C11.87 20.47 12.33 19.74 13.8 19.74C15.27 19.74 15.7 20.52 16.97 20.5C18.27 20.47 19.08 19.32 19.86 18.15C20.43 17.32 20.87 16.41 21.16 15.44C19.48 14.73 18.08 13.12 18.07 13.77Z" fill="white"/>
                  <path d="M15.5 4.37C16.15 3.58 16.56 2.54 16.44 1.5C15.56 1.54 14.5 2.09 13.82 2.87C13.21 3.57 12.72 4.63 12.85 5.64C13.83 5.72 14.83 5.18 15.5 4.37Z" fill="white"/>
                </svg>
                <div className="flex flex-col items-start">
                  <span style={{ fontFamily: 'popinsRegular', fontSize: '10px', color: 'rgba(255,255,255,0.6)' }}>DOWNLOAD ON THE</span>
                  <span style={{ fontFamily: 'popinsMedium', fontSize: '14px', color: '#ffffff' }}>App Store</span>
                </div>
              </a>

              {/* Google Play */}
              <a
                href="/"
                className="transition-all duration-300"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '12px',
                  background: '#1B1A28', border: '1px solid #E5E5E5',
                  borderRadius: '14px', padding: '14px 28px', textDecoration: 'none',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#3FC4E3'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(63,196,227,0.2)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E5E5'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; }}
              >
                <svg width="22" height="24" viewBox="0 0 22 24" fill="none" aria-hidden="true">
                  <path d="M1.22 0.5C0.9 0.68 0.67 1.04 0.67 1.52V22.48C0.67 22.96 0.9 23.32 1.22 23.5L1.32 23.59L12.84 12.07V11.93L1.32 0.41L1.22 0.5Z" fill="#3FC4E3"/>
                  <path d="M16.67 15.92L12.84 12.07V11.93L16.67 8.08L16.79 8.15L21.35 10.74C22.65 11.48 22.65 12.52 21.35 13.26L16.79 15.85L16.67 15.92Z" fill="#FE5C73"/>
                  <path d="M16.79 15.85L12.84 11.9L1.22 23.5C1.67 23.98 2.4 24.04 3.22 23.58L16.79 15.85Z" fill="#1B1A28"/>
                  <path d="M16.79 8.15L3.22 0.42C2.4 -0.04 1.67 0.02 1.22 0.5L12.84 12.1L16.79 8.15Z" fill="#006077"/>
                </svg>
                <div className="flex flex-col items-start">
                  <span style={{ fontFamily: 'popinsRegular', fontSize: '10px', color: 'rgba(255,255,255,0.6)' }}>GET IT ON</span>
                  <span style={{ fontFamily: 'popinsMedium', fontSize: '14px', color: '#ffffff' }}>Google Play</span>
                </div>
              </a>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
